import { Link, createFileRoute } from '@tanstack/react-router';

export const Route = createFileRoute('/privacy')({
  component: PrivacyPage,
});

const SUPPORT_EMAIL = 'support@pirouettehq.com';

function PrivacyPage() {
  return (
    <main
      id="main"
      className="min-h-dvh bg-[var(--color-surface-sunken)] px-6 py-10 text-[var(--color-ink)] sm:px-10 lg:px-16"
      data-testid="privacy-page"
    >
      <div className="mx-auto flex max-w-4xl flex-col gap-8">
        <nav aria-label="Public navigation" className="flex items-center justify-between gap-6">
          <Link
            to="/"
            className="font-[var(--font-display)] tracking-[var(--tracking-wide)] text-[var(--text-lg)] uppercase"
          >
            Pirouette
          </Link>
          <div className="flex items-center gap-5 text-[var(--text-sm)]">
            <Link to="/terms" className="text-[var(--color-ink-muted)] hover:underline">
              Terms
            </Link>
            <Link to="/contact" className="text-[var(--color-ink-muted)] hover:underline">
              Contact
            </Link>
          </div>
        </nav>

        <header>
          <p className="font-[var(--font-display)] tracking-[var(--tracking-wide)] text-[var(--color-accent)] text-[var(--text-base)] uppercase">
            Privacy
          </p>
          <h1 className="mt-4 leading-[var(--leading-tight)] font-[var(--font-display)] tracking-[var(--tracking-tight)] text-[var(--text-display)]">
            Pirouette privacy policy
          </h1>
          <p className="mt-4 text-[var(--color-ink-muted)]">Effective June 8, 2026</p>
        </header>

        <section className="space-y-6 rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface)] p-6 leading-[var(--leading-relaxed)] text-[var(--color-ink-muted)]">
          <div>
            <h2 className="font-semibold text-[var(--color-ink)] text-[var(--text-xl)]">
              Information we collect
            </h2>
            <p className="mt-2">
              Pirouette helps dance studios manage families, dancers, classes, attendance, payments,
              recital operations, messages, leads, trials, and related studio workflows. We collect
              information needed to provide those services, including account contact details,
              family and dancer profile information, class and enrollment records, attendance
              records, billing and payment metadata, communication preferences, delivery records,
              support requests, and operational audit logs.
            </p>
          </div>

          <div>
            <h2 className="font-semibold text-[var(--color-ink)] text-[var(--text-xl)]">
              How we use information
            </h2>
            <p className="mt-2">
              We use information to operate Pirouette, authenticate users, provide studio management
              features, process payments through connected payment providers, deliver email, in-app,
              push, and compliance-approved SMS notifications, provide support, protect the service,
              and maintain legal and operational records.
            </p>
          </div>

          <div>
            <h2 className="font-semibold text-[var(--color-ink)] text-[var(--text-xl)]">
              SMS and mobile information
            </h2>
            <p className="mt-2">
              SMS consent is optional. Mobile numbers and SMS opt-in data are used to deliver
              transactional Pirouette alerts only after consent and registration gates are
              satisfied. Mobile opt-in data and consent are not sold, rented, or shared with third
              parties for their marketing or promotional purposes.
            </p>
          </div>

          <div>
            <h2 className="font-semibold text-[var(--color-ink)] text-[var(--text-xl)]">
              Service providers
            </h2>
            <p className="mt-2">
              We use trusted providers to host the app, store data, process payments, deliver
              notifications, monitor reliability, and provide support. These providers may process
              information for Pirouette only as needed to provide their services to us.
            </p>
          </div>

          <div>
            <h2 className="font-semibold text-[var(--color-ink)] text-[var(--text-xl)]">
              Children and dancer data
            </h2>
            <p className="mt-2">
              Dance studios may manage records for minors. Pirouette is designed for studio and
              guardian-controlled access. Studios and guardians are responsible for ensuring they
              have the permissions needed to enter and manage dancer information.
            </p>
          </div>

          <div>
            <h2 className="font-semibold text-[var(--color-ink)] text-[var(--text-xl)]">
              Choices and requests
            </h2>
            <p className="mt-2">
              You may contact Pirouette for privacy, access, correction, deletion, or SMS consent
              questions at{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[var(--color-accent)]">
                {SUPPORT_EMAIL}
              </a>
              . SMS recipients may reply <strong>STOP</strong> to opt out or <strong>HELP</strong>{' '}
              for help.
            </p>
          </div>
        </section>
      </div>
    </main>
  );
}
